import TaskModel from 'models/TaskModel'
import Util from 'util/util'
import { formatNumber } from 'views/components/taskGroups/tasks/template40/parts/NumberUtil'
import { GAP_TYPE, getNumberBound } from 'views/components/taskGroups/tasks/template41/GapTextUtil'

// Collect the settings that determine which answers are accepted for a task, so publishers can check
// the material without access to the author environment. Returns null if the task type has no such settings.
//
// Returns an object with:
// - gradingMode: label of the grading mode of the task
// - gradingStrictness: label of the grading strictness of open inputs, or null if not applicable
// - items: list of { modelAnswer, isHtml, alternatives, rules, type } for each gap/input of the task
export function getModelAnswerSettings(model, { isExam = false } = {}) {
    const settings = getTemplateSettings(model)
    if (!settings) {
        return null
    }
    return {
        gradingMode: getGradingModeLabel(model.get('grading_mode'), isExam),
        ...settings,
    }
}

function getTemplateSettings(model) {
    const taskInfoJson = model.get('task_info_json') ?? {}
    const isAutoGraded = model.get('grading_mode') === 'auto'
    // Grading strictness only applies to open inputs of auto graded tasks.
    const legacyGradingStrictness = isAutoGraded ?
        getGradingStrictnessLabel(getLegacyGradingStrictness(taskInfoJson)) :
        null

    switch (model.get('template_id')) {
        case 2: {
            // Without a valid grading strictness the answer is not auto graded, see autoGrade() in AutoGradingUtil.
            const gradingStrictness = getGradingStrictnessLabel(taskInfoJson.options?.gradingStrictness)
            if (isAutoGraded && !gradingStrictness) {
                return null
            }
            return {
                gradingStrictness: isAutoGraded ? gradingStrictness : null,
                items: [{
                    modelAnswer: taskInfoJson.answer,
                    isHtml: true,
                    // Alternatives are only used for automatic grading.
                    alternatives: isAutoGraded ?
                        getAlternatives(taskInfoJson.answer, _.pluck(taskInfoJson.variants ?? [], 'text')) :
                        [],
                }],
            }
        }
        case 22: {
            const gapCells = _.sortBy(
                (taskInfoJson.gridCells ?? []).filter((cell) => cell.isGapCell),
                (cell) => cell.rowIndex * 1000 + cell.columnIndex
            )
            return {
                gradingStrictness: legacyGradingStrictness,
                items: gapCells.map((cell) => {
                    const modelAnswer = Util.stripTags(cell.cellText ?? '')
                    return {
                        modelAnswer,
                        alternatives: getAlternatives(modelAnswer, _.pluck(cell.variants ?? [], 'text')),
                    }
                }),
            }
        }
        case 25: {
            // Drag hotspots only have the model answers, which are already shown by the answer of the task.
            if (taskInfoJson.answerType !== 'fillText') {
                return null
            }
            return {
                gradingStrictness: legacyGradingStrictness,
                items: (taskInfoJson.answers ?? []).map((answer) => {
                    const modelAnswer = Util.unescape(answer.label ?? '')
                    return {
                        modelAnswer,
                        alternatives: getAlternatives(modelAnswer, _.pluck(answer.variants ?? [], 'text')),
                    }
                }),
            }
        }
        case 31: {
            return {
                gradingStrictness: legacyGradingStrictness,
                items: getGapFragments(taskInfoJson.text).map((fragment) => {
                    const [modelAnswer, ...alternatives] = _.pluck(fragment.answers, 'text')
                    return {
                        modelAnswer,
                        alternatives: getAlternatives(modelAnswer, alternatives),
                    }
                }),
            }
        }
        case 40: {
            const rules = []
            if (['absolute', 'percentage'].includes(taskInfoJson.marginType) && taskInfoJson.margin) {
                const margin = taskInfoJson.marginType === 'percentage' ?
                    Math.abs(taskInfoJson.answer * (taskInfoJson.margin / 100)) :
                    Math.abs(taskInfoJson.margin)
                rules.push(getDeviationRule(
                    taskInfoJson.marginType,
                    formatNumber(taskInfoJson.answer - margin),
                    formatNumber(taskInfoJson.answer + margin)
                ))
            } else {
                rules.push(window.i18n.gettext('No deviation'))
            }
            return {
                gradingStrictness: null,
                items: [{
                    modelAnswer: formatNumber(taskInfoJson.answer),
                    alternatives: [],
                    rules,
                }],
            }
        }
        case 41: {
            const gapFragments = getGapFragments(taskInfoJson.text)
            const openGapFragment = gapFragments.find((fragment) => fragment.type === GAP_TYPE.OPEN)
            return {
                // Gaps without a grading strictness are graded strictly by Template41.
                gradingStrictness: isAutoGraded && openGapFragment ?
                    getGradingStrictnessLabel(openGapFragment.gradingStrictness ?? TaskModel.CASE_SENSITIVE_GRADING) :
                    null,
                items: gapFragments.map(getGapTextItem),
            }
        }
        default:
            return null
    }
}

function getGapTextItem(fragment) {
    return {
        ...getGapTextAnswers(fragment),
        type: getGapTypeLabel(fragment.type),
    }
}

function getGapTextAnswers(fragment) {
    const correctAnswers = _.pluck(fragment.answers.filter(({correct}) => correct), 'text')

    if (fragment.type === GAP_TYPE.NUMBER) {
        const answer = fragment.answers[0]
        const rules = []
        if (['absolute', 'percentage'].includes(answer.marginType) && answer.margin) {
            rules.push(getDeviationRule(
                answer.marginType,
                formatNumber(getNumberBound(answer, true)),
                formatNumber(getNumberBound(answer, false))
            ))
        } else {
            rules.push(window.i18n.gettext('No deviation'))
        }
        rules.push(getDecimalsRule(answer.format))
        return {
            modelAnswer: answer.text,
            alternatives: [],
            rules,
        }
    }

    if (fragment.type === GAP_TYPE.OPEN) {
        return {
            modelAnswer: correctAnswers[0],
            alternatives: getAlternatives(correctAnswers[0], correctAnswers.slice(1)),
        }
    }

    // Choice and drag gaps have exactly one correct answer, which may contain HTML.
    return {
        modelAnswer: correctAnswers[0],
        isHtml: true,
        alternatives: [],
    }
}

function getGapFragments(fragments = []) {
    return fragments.filter((fragment) => fragment instanceof Object)
}

// Remove the model answer itself and duplicates from the list of alternatives.
function getAlternatives(modelAnswer, alternatives) {
    const modelAnswerText = Util.stripTags(modelAnswer ?? '').trim()
    return _.uniq(alternatives.filter((alternative) => alternative && alternative.trim() !== modelAnswerText))
}

// Keep compatibility with the old 'caseSensitive' grading, like the task templates do.
function getLegacyGradingStrictness(taskInfoJson) {
    const { gradingStrictness, caseSensitive } = taskInfoJson.options ?? {}
    if (gradingStrictness !== undefined) {
        return gradingStrictness
    }
    return caseSensitive ? TaskModel.CASE_SENSITIVE_GRADING : TaskModel.CASE_INSENSITIVE_GRADING
}

// Use the same labels as the grading mode input of the author environment.
function getGradingModeLabel(gradingMode, isExam) {
    switch (gradingMode) {
        case 'auto':
            return window.i18n.gettext('Automatic grading')
        case 'student':
            // Exams don't support student grading, so the teacher grades instead.
            return isExam ? window.i18n.gettext('Teacher grading') : window.i18n.gettext('Student grading')
        default:
            return window.i18n.gettext('Don\'t grade')
    }
}

// Use the same labels as the gap type input of the gap editor.
function getGapTypeLabel(gapType) {
    switch (gapType) {
        case GAP_TYPE.NUMBER:
            return window.i18n.gettext('Number')
        case GAP_TYPE.CHOICE:
            return window.i18n.gettext('Choice')
        case GAP_TYPE.DRAG:
            return window.i18n.gettext('Drag')
        default:
            return window.i18n.gettext('Open')
    }
}

// Use the same labels as the grading strictness input of the author environment.
function getGradingStrictnessLabel(gradingStrictness) {
    switch (gradingStrictness) {
        case TaskModel.CASE_SENSITIVE_GRADING:
            return window.i18n.gettext('Strict spelling')
        case TaskModel.CASE_INSENSITIVE_GRADING:
            return window.i18n.gettext('Case insensitive')
        case TaskModel.TEXT_SIMILARITY_GRADING:
            return window.i18n.gettext('Allow small spelling errors')
        default:
            return null
    }
}

function getDeviationRule(marginType, lowerBound, upperBound) {
    const marginTypeLabel = marginType === 'percentage' ?
        window.i18n.gettext('Percentage deviation') :
        window.i18n.gettext('Absolute deviation')
    return `${marginTypeLabel}: ${lowerBound} – ${upperBound}`
}

// Use the same labels as the decimals input of the gap editor.
function getDecimalsRule(format) {
    if (format?.key === 'currency') {
        return window.i18n.gettext('Currency (0 or 2 decimals and ,- allowed)')
    }
    if (Number.isInteger(format?.value)) {
        return window.i18n.sprintf(
            window.i18n.ngettext('Exactly %s decimal', 'Exactly %s decimals', format.value),
            format.value
        )
    }
    return window.i18n.gettext('Free number of decimals')
}
